import { useCallback, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../../shared/auth/useCompanyWriteAccess';
import './BalanceClosingPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    title: 'Bilanco Hesaplari Donem Sonu Kontrolu ve Duzeltmeler',
    description: "60-68 gelir/gider hesaplarini 690'a kapatir, 690 bakiyesini 590 (kar) veya 591 (zarar) hesabina aktarir. trial_balance_entries degistirilmez. Kapanis sonrasi gelir tablosu raporu kapanis fisleri haric tutularak gosterilir.",
    runAnalysis: 'Analiz Calistir',
    running: 'Analiz yapiliyor...',
    apply: 'Fisleri Olustur ve Uygula',
    applying: 'Uygulaniyor...',
    rollback: 'Geri Al',
    rollbackConfirm: 'Bilanco donem sonu kapanis fisleri geri alinacak. Devam edilsin mi?',
    analysisFailed: 'Analiz basarisiz.',
    analysisError: 'Analiz sirasinda hata olustu.',
    applyFailed: 'Fisler olusturulamadi.',
    applyError: 'Fis olusturma sirasinda hata olustu.',
    rollbackFailed: 'Geri alma basarisiz.',
    rollbackError: 'Geri alma sirasinda hata olustu.',
    applySuccess: '{{voucherCount}} fis olusturuldu, {{lineCount}} satir. Kebir mizan yeniden hesaplandi.',
    summaryTotal: 'Toplam bulgu',
    summaryAuto: 'Otomatik',
    summaryManual: 'Manuel',
    summary690: '690 Donem Sonucu',
    summaryTransfer: "590/591'e aktarilacak tutar",
    findingsTitle: 'Kapatilacak Hesaplar',
    colAccountCode: 'Hesap Kodu',
    colAccountName: 'Hesap Adi',
    colType: 'Tip',
    colNet: 'Net Bakiye',
    colCloseAmount: 'Kapatilacak Tutar',
    colStatus: 'Durum',
    colReason: 'Aciklama',
    auto: 'Otomatik',
    manual: 'Manuel',
    empty: '60-68 araliginda kapatilacak bakiye tespit edilmedi.',
    readOnlyAccess: 'Bu sirket icin yazma yetkiniz yok.'
  },
  'en-US': {
    title: 'Balance Sheet Period-End Control and Adjustments',
    description: 'Closes 60-68 income/expense accounts to 690, then transfers 690 balance to 590 (profit) or 591 (loss). trial_balance_entries are not modified. After closing, income statement reports exclude closing vouchers.',
    runAnalysis: 'Run Analysis',
    running: 'Running analysis...',
    apply: 'Create and Apply Vouchers',
    applying: 'Applying...',
    rollback: 'Rollback',
    rollbackConfirm: 'Balance sheet period-end closing vouchers will be rolled back. Continue?',
    analysisFailed: 'Analysis failed.',
    analysisError: 'An error occurred during analysis.',
    applyFailed: 'Vouchers could not be created.',
    applyError: 'An error occurred while creating vouchers.',
    rollbackFailed: 'Rollback failed.',
    rollbackError: 'An error occurred during rollback.',
    applySuccess: '{{voucherCount}} vouchers created, {{lineCount}} lines. Trial balance recalculated.',
    summaryTotal: 'Total findings',
    summaryAuto: 'Auto',
    summaryManual: 'Manual',
    summary690: '690 Period Result',
    summaryTransfer: 'Amount to transfer to 590/591',
    findingsTitle: 'Accounts to Close',
    colAccountCode: 'Account Code',
    colAccountName: 'Account Name',
    colType: 'Type',
    colNet: 'Net Balance',
    colCloseAmount: 'Closing Amount',
    colStatus: 'Status',
    colReason: 'Description',
    auto: 'Auto',
    manual: 'Manual',
    empty: 'No balance to close found in 60-68 range.',
    readOnlyAccess: 'You do not have write access for this company.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

function BalanceClosingPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat(localeKey, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }), [localeKey]);

  const formatCurrency = useCallback((value) => {
    if (value == null || Number.isNaN(Number(value))) return currencyFormatter.format(0);
    return currencyFormatter.format(Number(value));
  }, [currencyFormatter]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [analysis, setAnalysis] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const { canWrite } = useCompanyWriteAccess(selection.companyId);

  const runAnalysis = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) return;
    setLoading(true);
    setError('');
    setApplyResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/financials/balance-closing/analyze?companyId=${selection.companyId}&periodId=${selection.periodId}`, { headers: buildHeaders() });
      if (!res.ok) {
        const textError = await res.text();
        throw new Error(textError || t('analysisFailed'));
      }
      const data = await res.json();
      setAnalysis(data);
    } catch (e) {
      setError(e?.message || t('analysisError'));
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [selection.companyId, selection.periodId, t]);

  const handleApply = useCallback(async () => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    if (!selection.companyId || !selection.periodId) return;
    setProcessing(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/financials/balance-closing/apply?companyId=${selection.companyId}&periodId=${selection.periodId}`, { method: 'POST', headers: buildHeaders() });
      if (!res.ok) {
        let msg = t('applyFailed');
        try {
          const json = await res.json();
          if (json?.message) msg = json.message;
        } catch {
          const textError = await res.text();
          if (textError) msg = textError;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setApplyResult(data);
      runAnalysis();
    } catch (e) {
      setError(e?.message || t('applyError'));
    } finally {
      setProcessing(false);
    }
  }, [canWrite, selection.companyId, selection.periodId, runAnalysis, t]);

  const handleRollback = useCallback(async () => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    if (!selection.companyId || !selection.periodId) return;
    if (!window.confirm(t('rollbackConfirm'))) return;
    setProcessing(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/financials/balance-closing/rollback?companyId=${selection.companyId}&periodId=${selection.periodId}`, { method: 'POST', headers: buildHeaders() });
      if (!res.ok) throw new Error(t('rollbackFailed'));
      setApplyResult(null);
      runAnalysis();
    } catch (e) {
      setError(e?.message || t('rollbackError'));
    } finally {
      setProcessing(false);
    }
  }, [canWrite, selection.companyId, selection.periodId, runAnalysis, t]);

  const findings = analysis?.findings ?? [];
  const autoFindings = findings.filter((f) => f.status === 'AUTO');
  const canApply = autoFindings.length > 0 || (analysis?.transferTo590Or591 ?? 0) > 0;

  return (
    <div className="balance-closing">
      <PageHeader title={t('title')} description={t('description')} />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      <section className="balance-closing__actions">
        <button type="button" className="btn-primary" onClick={runAnalysis} disabled={!selection.companyId || !selection.periodId || loading}>
          {loading ? t('running') : t('runAnalysis')}
        </button>
        {canApply && (
          <button type="button" className="btn-primary" onClick={handleApply} disabled={!canWrite || processing}>
            {processing ? t('applying') : t('apply')}
          </button>
        )}
        {(applyResult?.vouchersCreated > 0 || (analysis?.activeClosingVoucherCount ?? 0) > 0) && (
          <button type="button" className="btn-secondary" onClick={handleRollback} disabled={!canWrite || processing}>{t('rollback')}</button>
        )}
      </section>

      {error && <p className="balance-closing__error">{error}</p>}

      {applyResult && applyResult.vouchersCreated > 0 && (
        <div className="balance-closing__success">
          {t('applySuccess')
            .replace('{{voucherCount}}', String(applyResult.vouchersCreated))
            .replace('{{lineCount}}', String(applyResult.linesCreated))}
        </div>
      )}

      {analysis && (
        <section className="balance-closing__summary">
          <p>
            <strong>{t('summaryTotal')}:</strong> {analysis.totalCount} | {t('summaryAuto')}: {analysis.autoCount} | {t('summaryManual')}: {analysis.manualCount}
          </p>
          <p>
            <strong>{t('summary690')}:</strong> {formatCurrency(analysis.account690Balance)} ({analysis.periodResultLabel})
          </p>
          {analysis.transferTo590Or591 > 0 && (
            <p>
              <strong>{t('summaryTransfer')}:</strong> {formatCurrency(analysis.transferTo590Or591)}
            </p>
          )}
        </section>
      )}

      {analysis && findings.length > 0 && (
        <section className="balance-closing__table-wrapper">
          <h3>{t('findingsTitle')}</h3>
          <table className="balance-closing__table">
            <thead>
              <tr>
                <th>{t('colAccountCode')}</th>
                <th>{t('colAccountName')}</th>
                <th>{t('colType')}</th>
                <th>{t('colNet')}</th>
                <th className="balance-closing__align-right">{t('colCloseAmount')}</th>
                <th>{t('colStatus')}</th>
                <th>{t('colReason')}</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => (
                <tr key={`${f.accountCode}-${i}`}>
                  <td>{f.accountCode}</td>
                  <td>{f.accountName ?? '-'}</td>
                  <td>{f.type}</td>
                  <td>{formatCurrency(f.netBalance)}</td>
                  <td className="balance-closing__align-right">{formatCurrency(f.closeAmount)}</td>
                  <td>
                    <span className={f.status === 'AUTO' ? 'balance-closing__tag ok' : 'balance-closing__tag manual'}>
                      {f.status === 'AUTO' ? t('auto') : t('manual')}
                    </span>
                  </td>
                  <td>{f.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {analysis && findings.length === 0 && (
        <div className="balance-closing__empty"><p>{t('empty')}</p></div>
      )}
    </div>
  );
}

export default BalanceClosingPage;
